import { type JwtPayload, jwtDecode } from 'jwt-decode'
import {
  type SilentRenewErrorCallback,
  type UserLoadedCallback,
  type UserManager,
  type UserUnloadedCallback,
  WebStorageStateStore
} from 'oidc-client-ts'
import { LogLevel, type OidcClient, setupOidcClient } from 'piral-oidc'
import { useCallback, useEffect, useRef } from 'react'
import { environmentConfig } from '../../environmentConfig'
import { loadScript } from './loadScript'
import userProfile, { MOCK_USER, ROLES } from './userProfile'

if (environmentConfig.OIDC_MODE === 'POLYFILL') {
  // see docker build
  loadScript('/oidc-polyfill.js')
    .then(() => console.warn('OIDC polyfill loaded'))
    .catch((e) => console.error('OIDC polyfill not loaded', e))
}

const createOidcClient = () => {
  if (__MOCK__) {
    return {
      _: {},
      login: async () => {},
      logout: async () => {},
      revoke: async () => {},
      handleAuthentication: async () => {
        return {
          shouldRender: true,
          state: 'OK'
        }
      },
      account: async () => {
        return {
          iss: 'iss',
          sub: 'sub',
          aud: 'aud',
          exp: 100000,
          iat: 1000000,
          name: MOCK_USER.name,
          given_name: MOCK_USER.givenName,
          family_name: MOCK_USER.familyName
        }
      },
      token: async () => {
        return 'MOCK-TOKEN'
      },
      extendHeaders: () => {}
    } satisfies OidcClient
  }

  return setupOidcClient({
    clientId: environmentConfig.OIDC_CLIENT,
    identityProviderUri: environmentConfig.OIDC_URL,
    responseType: 'code',
    signInRedirectParams: {
      state: {
        finalRedirectUri: globalThis.location.href
      }
    },
    scopes: ['openid'],
    logLevel: LogLevel.error,
    userStore: new WebStorageStateStore({ store: globalThis.localStorage })
  })
}
const oidcClient = createOidcClient()
export default oidcClient

const clearSessionData = () => {
  Object.keys(globalThis.localStorage)
    .filter((key) => key.startsWith('oidc.'))
    .forEach((key) => {
      globalThis.localStorage.removeItem(key)
    })
}

export const logout = () => {
  console.debug('logout requested')
  oidcClient
    .logout()
    .then(() => {
      console.debug('logout handled')
    })
    .catch((e) => {
      console.error(
        'Error while logging out. A page reload will be triggered so the browser should redirect to login page',
        e
      )
      clearSessionData()
      globalThis.location.reload()
    })
}

type KeycloakPayload = JwtPayload & {
  realm_access?: { roles: string[] }
  preferred_username: string
  name: string
  given_name: string
  family_name: string
  email: string
}

export const useOidc = (visible: boolean) => {
  const tokenScheduled = useRef(false)

  const oidcTokenUpdated = useCallback((token: string | null) => {
    try {
      if (token) {
        const jwt = jwtDecode<KeycloakPayload>(token)
        const realmRoles = jwt?.realm_access?.roles || []
        userProfile.value = {
          id: jwt.preferred_username,
          name: jwt.name,
          givenName: jwt.given_name,
          familyName: jwt.family_name,
          email: jwt.email,
          roles: ROLES.filter((role) => realmRoles.includes(role))
        }
      } else {
        userProfile.value = null
      }
      console.debug('user updated:', userProfile.value)
    } catch (e) {
      console.error('failed token updated', e)
    }
  }, [])

  const getToken = useCallback(() => {
    if (globalThis.location.pathname !== '/auth') {
      console.debug('get token')
      oidcClient
        .token()
        .then((token) => {
          oidcTokenUpdated(token)
        })
        .catch((e) => {
          console.error('error', e)
          oidcClient
            .logout()
            .then(() => {
              console.debug('logout successful')
            })
            .catch((e) => {
              console.error('error logout', e)
            })
        })
    } else {
      console.debug('auth page')
    }
  }, [oidcTokenUpdated])

  useEffect(() => {
    if (__MOCK__) {
      userProfile.value = MOCK_USER
      return
    }
    if (!visible) {
      return
    }

    const um = oidcClient._ as UserManager
    console.debug('register oidc event handler')

    const userLoadedHandler: UserLoadedCallback = (user) => {
      console.debug('user loaded')
      oidcTokenUpdated(user.access_token)
    }
    const userUnloadedHandler: UserUnloadedCallback = () => {
      console.debug('user unloaded')
      oidcTokenUpdated(null)
      clearSessionData()
    }
    const accessTokenExpiredHandler = () => {
      if (globalThis.navigator.onLine) {
        console.debug('token expired')
        getToken()
      } else {
        console.debug('token expired, scheduled')
        tokenScheduled.current = true
      }
    }
    const silentRenewErrorHandler: SilentRenewErrorCallback = () => {
      if (!navigator.onLine) {
        console.log('Silent renew failed due to offline status - will retry when online')
        return // ignore error if offline
      }

      setTimeout(async () => {
        const user = await um.getUser()
        if (user && (!user.expired || user.refresh_token)) {
          um.stopSilentRenew()
          um.startSilentRenew()
        } else {
          // Token abgelaufen und kein Refresh Token - Neuanmeldung erforderlich
          console.error('Silent renew failed, re-authentication required')
        }
      }, 5000)
    }

    const offlineHandler = () => {
      console.debug('Network offline - stopping silent renew')
      um.stopSilentRenew()
    }
    const onlineHandler = async () => {
      console.log('Network online - restarting silent renew')
      const user = await um.getUser()

      if (user && (!user.expired || user.refresh_token)) {
        um.stopSilentRenew()
        um.startSilentRenew()
      }

      if (tokenScheduled.current) {
        getToken()
        tokenScheduled.current = false
      }
    }
    const visibilityChangeHandler = async () => {
      if (!document.hidden && navigator.onLine) {
        const user = await um.getUser()

        if (user?.expired && user.refresh_token) {
          um.stopSilentRenew()
          um.startSilentRenew()
        }
      }
    }

    um.events.addUserLoaded(userLoadedHandler)
    um.events.addUserUnloaded(userUnloadedHandler)
    um.events.addAccessTokenExpired(accessTokenExpiredHandler)
    um.events.addSilentRenewError(silentRenewErrorHandler)

    globalThis.addEventListener('offline', offlineHandler)
    globalThis.addEventListener('online', onlineHandler)
    globalThis.document.addEventListener('visibilitychange', visibilityChangeHandler)

    getToken()

    return () => {
      console.debug('unregister oidc/navigator event handler')

      globalThis.document.removeEventListener('visibilitychange', visibilityChangeHandler)
      globalThis.removeEventListener('online', onlineHandler)
      globalThis.removeEventListener('offline', offlineHandler)

      um.events.removeUserLoaded(userLoadedHandler)
      um.events.removeUserUnloaded(userUnloadedHandler)
      um.events.removeAccessTokenExpired(accessTokenExpiredHandler)
      um.events.removeSilentRenewError(silentRenewErrorHandler)
    }
  }, [getToken, visible, oidcTokenUpdated])

  return { oidcClient }
}
